import * as THREE from 'three';
import { CONFIG, ObstacleKind, PickupKind } from './config.js';
import { pick, randInt } from '../utils/math.js';

function makeTrackSegment(mats, length) {
  const g = new THREE.Group();
  const bed = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.25, length), mats.track);
  bed.position.y = -0.12;
  bed.receiveShadow = true;
  g.add(bed);

  for (const lx of CONFIG.laneXs) {
    for (const ox of [-0.35, 0.35]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, length), mats.rail);
      rail.position.set(lx + ox, 0.02, 0);
      g.add(rail);
    }
  }

  const sleeperCount = Math.floor(length / 2);
  for (let i = 0; i < sleeperCount; i++) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(6.4, 0.08, 0.35), mats.sleeper);
    s.position.set(0, -0.02, -length / 2 + 1 + i * 2);
    g.add(s);
  }

  // side buildings (procedural blocks)
  for (const side of [-1, 1]) {
    const h = 4 + Math.random() * 10;
    const b = new THREE.Mesh(new THREE.BoxGeometry(3.5, h, length * 0.9), mats.building);
    b.position.set(side * 7.2, h / 2 - 0.2, 0);
    g.add(b);
    if (Math.random() > 0.35) {
      const w = new THREE.Mesh(new THREE.BoxGeometry(0.2, h * 0.55, length * 0.35), mats.window);
      w.position.set(side * 5.35, h * 0.35, 0);
      g.add(w);
    }
  }
  return g;
}

function makeTrain(mats) {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.8, 2.0, 8), mats.train);
  body.position.y = 1.05;
  body.castShadow = true;
  g.add(body);
  const roof = new THREE.Mesh(new THREE.BoxGeometry(1.85, 0.15, 8.05), mats.trainRoof);
  roof.position.y = 2.15;
  g.add(roof);
  // windows strip
  const win = new THREE.Mesh(
    new THREE.BoxGeometry(1.82, 0.45, 6),
    new THREE.MeshStandardMaterial({ color: 0x0ea5e9, emissive: 0x0369a1, emissiveIntensity: 0.6 })
  );
  win.position.set(0, 1.35, 0);
  g.add(win);
  g.userData.kind = ObstacleKind.TRAIN;
  g.userData.halfL = 4;
  g.userData.height = 2.2;
  g.userData.topY = 2.2;
  return g;
}

function makeBarrierLow(mats) {
  const g = new THREE.Group();
  const bar = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.55, 0.45), mats.barrierLow);
  bar.position.y = 0.35;
  bar.castShadow = true;
  g.add(bar);
  const postL = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.7, 0.12), mats.rail);
  postL.position.set(-0.75, 0.35, 0);
  const postR = postL.clone();
  postR.position.x = 0.75;
  g.add(postL, postR);
  g.userData.kind = ObstacleKind.BARRIER_LOW;
  g.userData.halfL = 0.35;
  g.userData.height = 0.7;
  return g;
}

function makeBarrierHigh(mats) {
  const g = new THREE.Group();
  const bar = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.5, 0.4), mats.barrierHigh);
  bar.position.y = 1.35;
  bar.castShadow = true;
  g.add(bar);
  for (const x of [-0.7, 0.7]) {
    const p = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.5, 0.14), mats.rail);
    p.position.set(x, 0.75, 0);
    g.add(p);
  }
  g.userData.kind = ObstacleKind.BARRIER_HIGH;
  g.userData.halfL = 0.3;
  g.userData.height = 1.6;
  g.userData.clearance = 0.9; // roll under if height of player under this
  return g;
}

function makeCoin(mats) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.08, 16), mats.coin);
  mesh.rotation.z = Math.PI / 2;
  mesh.castShadow = true;
  mesh.userData.kind = PickupKind.COIN;
  return mesh;
}

function makeMagnetPickup(mats) {
  const mesh = new THREE.Mesh(new THREE.TorusGeometry(0.35, 0.12, 8, 16), mats.magnet);
  mesh.userData.kind = PickupKind.MAGNET;
  return mesh;
}

function makeHoverPickup(mats) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.12, 0.9), mats.hoverPickup);
  mesh.userData.kind = PickupKind.HOVERBOARD;
  return mesh;
}

export class World {
  constructor(scene, materials) {
    this.scene = scene;
    this.mats = materials;
    this.chunks = [];
    this.obstacles = [];
    this.pickups = [];
    this.nextChunkZ = 0;
    this.difficulty = 0;

    this.root = new THREE.Group();
    scene.add(this.root);

    // static ground plane under everything
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(80, 4000),
      materials.ground
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, -0.2, 1000);
    ground.receiveShadow = true;
    this.root.add(ground);
  }

  reset() {
    for (const c of this.chunks) this.root.remove(c.group);
    for (const o of this.obstacles) this.root.remove(o.mesh);
    for (const p of this.pickups) this.root.remove(p.mesh);
    this.chunks = [];
    this.obstacles = [];
    this.pickups = [];
    this.nextChunkZ = 0;
    this.difficulty = 0;
    // spawn starting empty chunks
    for (let i = 0; i < CONFIG.chunksAhead; i++) this.spawnChunk(i < 2);
  }

  spawnChunk(safe = false) {
    const z0 = this.nextChunkZ;
    const length = CONFIG.chunkLength;
    const mid = z0 + length / 2;
    const group = makeTrackSegment(this.mats, length);
    group.position.z = mid;
    this.root.add(group);
    this.chunks.push({ group, z0, z1: z0 + length });

    if (!safe) {
      this.populateChunk(z0, length);
    } else {
      // a few coins in intro
      for (let i = 0; i < 5; i++) {
        this.addCoin(CONFIG.laneXs[1], 0.9, z0 + 10 + i * 4);
      }
    }

    this.nextChunkZ += length;
    this.difficulty = Math.min(1, this.nextChunkZ / 2500);
  }

  populateChunk(z0, length) {
    const patterns = [
      () => this.patternSingleBarrier(z0, length),
      () => this.patternTrainBlock(z0, length),
      () => this.patternMixed(z0, length),
      () => this.patternCoinSnake(z0, length),
    ];
    pick(patterns)();

    // occasional powerups
    if (Math.random() < 0.18 + this.difficulty * 0.1) {
      const lane = randInt(0, 2);
      const z = z0 + 8 + Math.random() * (length - 16);
      if (Math.random() < 0.55) this.addPickup(PickupKind.MAGNET, CONFIG.laneXs[lane], 1.0, z);
      else this.addPickup(PickupKind.HOVERBOARD, CONFIG.laneXs[lane], 0.6, z);
    }
  }

  patternSingleBarrier(z0, length) {
    const z = z0 + 12 + Math.random() * (length - 20);
    const lane = randInt(0, 2);
    const kind = Math.random() < 0.5 ? ObstacleKind.BARRIER_LOW : ObstacleKind.BARRIER_HIGH;
    this.addObstacle(kind, lane, z);
    this.scatterCoinsNear(lane, z);
  }

  patternTrainBlock(z0, length) {
    const z = z0 + 14 + Math.random() * (length - 24);
    // block 1–2 lanes with trains
    const lanes = [0, 1, 2].sort(() => Math.random() - 0.5);
    const count = Math.random() < 0.45 + this.difficulty * 0.3 ? 2 : 1;
    for (let i = 0; i < count; i++) this.addObstacle(ObstacleKind.TRAIN, lanes[i], z);
    // coins on free lane
    const free = lanes.slice(count);
    for (const fl of free) {
      for (let i = 0; i < 4; i++) this.addCoin(CONFIG.laneXs[fl], 0.9, z - 6 + i * 3);
    }
  }

  patternMixed(z0, length) {
    const z1 = z0 + 10;
    const z2 = z0 + 26;
    this.addObstacle(Math.random() < 0.5 ? ObstacleKind.BARRIER_HIGH : ObstacleKind.BARRIER_LOW, randInt(0, 2), z1);
    this.addObstacle(ObstacleKind.TRAIN, randInt(0, 2), z2);
    this.patternCoinSnake(z0, length * 0.5);
  }

  patternCoinSnake(z0, length) {
    let lane = 1;
    const steps = 8;
    for (let i = 0; i < steps; i++) {
      if (Math.random() < 0.45) lane = Math.max(0, Math.min(2, lane + (Math.random() < 0.5 ? -1 : 1)));
      this.addCoin(CONFIG.laneXs[lane], 0.9, z0 + 6 + i * (length / (steps + 1)));
    }
  }

  scatterCoinsNear(lane, z) {
    for (let i = 0; i < 3; i++) {
      this.addCoin(CONFIG.laneXs[lane], 0.9, z - 8 + i * 2.5);
    }
  }

  addObstacle(kind, laneIndex, z) {
    let mesh;
    if (kind === ObstacleKind.TRAIN) mesh = makeTrain(this.mats);
    else if (kind === ObstacleKind.BARRIER_LOW) mesh = makeBarrierLow(this.mats);
    else mesh = makeBarrierHigh(this.mats);
    mesh.position.set(CONFIG.laneXs[laneIndex], 0, z);
    this.root.add(mesh);
    this.obstacles.push({
      mesh,
      kind,
      lane: laneIndex,
      z,
      halfL: mesh.userData.halfL,
      height: mesh.userData.height,
      topY: mesh.userData.topY || 0,
      alive: true,
    });
  }

  addCoin(x, y, z) {
    const mesh = makeCoin(this.mats);
    mesh.position.set(x, y, z);
    this.root.add(mesh);
    this.pickups.push({ mesh, kind: PickupKind.COIN, x, y, z, alive: true });
  }

  addPickup(kind, x, y, z) {
    const mesh = kind === PickupKind.MAGNET ? makeMagnetPickup(this.mats) : makeHoverPickup(this.mats);
    mesh.position.set(x, y, z);
    this.root.add(mesh);
    this.pickups.push({ mesh, kind, x, y, z, alive: true });
  }

  update(playerZ) {
    while (this.nextChunkZ < playerZ + CONFIG.chunkLength * CONFIG.chunksAhead) {
      this.spawnChunk(false);
    }

    // cleanup ~300 behind
    const cut = playerZ - CONFIG.cleanupDistance;
    this.chunks = this.chunks.filter((c) => {
      if (c.z1 < cut) {
        this.root.remove(c.group);
        return false;
      }
      return true;
    });
    this.obstacles = this.obstacles.filter((o) => {
      if (!o.alive || o.z < cut) {
        this.root.remove(o.mesh);
        return false;
      }
      return true;
    });
    this.pickups = this.pickups.filter((p) => {
      if (!p.alive || p.z < cut) {
        this.root.remove(p.mesh);
        return false;
      }
      // spin coins
      if (p.kind === PickupKind.COIN) p.mesh.rotation.y += 0.08;
      else p.mesh.rotation.y += 0.04;
      return true;
    });
  }

  isTrainTopAt(x, z) {
    for (const o of this.obstacles) {
      if (!o.alive || o.kind !== ObstacleKind.TRAIN) continue;
      if (Math.abs(o.mesh.position.x - x) > 1.0) continue;
      if (Math.abs(o.z - z) <= o.halfL - 0.2) return true;
    }
    return false;
  }

  /**
   * Returns { hit, pickup } results for this frame.
   */
  collide(player, magnetOn) {
    const b = player.getBounds();
    let hit = null;
    const gathered = [];

    for (const o of this.obstacles) {
      if (!o.alive) continue;
      if (Math.abs(o.mesh.position.x - b.x) > 1.05) continue;
      if (Math.abs(o.z - b.z) > o.halfL + 0.45) continue;

      if (o.kind === ObstacleKind.BARRIER_LOW) {
        // jump over clears; standing/rolling hits unless jumping high enough
        if (b.y + (b.rolling ? 0.2 : 0.5) > 0.85) continue;
        hit = o;
        break;
      }
      if (o.kind === ObstacleKind.BARRIER_HIGH) {
        // roll under clears; jumping into bar hits; standing hits
        if (b.rolling && b.y < 0.2) continue;
        if (b.y > 1.7) continue;
        hit = o;
        break;
      }
      if (o.kind === ObstacleKind.TRAIN) {
        // on top is fine; otherwise collide unless jumping clear over (rare) / invuln
        if (b.y >= o.topY - 0.15) continue;
        if (b.y > o.height + 0.3) continue;
        hit = o;
        break;
      }
    }

    const magnetR = magnetOn ? CONFIG.magnetRadius : 1.1;
    for (const p of this.pickups) {
      if (!p.alive) continue;
      const dx = p.mesh.position.x - b.x;
      const dy = p.mesh.position.y - (b.y + 0.8);
      const dz = p.z - b.z;
      const dist = Math.hypot(dx, dy, dz);
      const reach = p.kind === PickupKind.COIN ? magnetR : 1.2;
      if (dist < reach) {
        // magnet pull visual
        if (magnetOn && p.kind === PickupKind.COIN && dist > 0.6) {
          p.mesh.position.x = THREE.MathUtils.lerp(p.mesh.position.x, b.x, 0.35);
          p.mesh.position.y = THREE.MathUtils.lerp(p.mesh.position.y, b.y + 0.9, 0.35);
          p.mesh.position.z = THREE.MathUtils.lerp(p.mesh.position.z, b.z, 0.35);
          p.z = p.mesh.position.z;
          if (Math.hypot(p.mesh.position.x - b.x, p.mesh.position.z - b.z) > 0.55) continue;
        }
        p.alive = false;
        this.root.remove(p.mesh);
        gathered.push(p);
      }
    }

    return { hit, gathered };
  }
}
